import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import {
  PoPageModule,
  PoButtonModule,
  PoTableModule,
  PoTableColumn,
  PoTableComponent,
  PoPageAction,
  PoPageFilter,
  PoLoadingModule,
  PoNotificationService,
  PoTagModule,
  PoPageSlideModule,
  PoPageSlideComponent,
  PoFieldModule,
  PoSelectOption
} from '@po-ui/ng-components';

import { NotaPendente, NotasPendentesFiltros, NotasPendentesService } from './notas-pendentes.service';
import { LoginService } from '../../core/services/login.service';
import { FilialService } from '../../core/services/filial.service';
import { limparNumeroNf } from '../../shared/numero-utils';

type NotaRow = NotaPendente & { $selected?: boolean; numeroExibe?: string };

@Component({
  selector: 'app-notas-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PoPageModule,
    PoButtonModule,
    PoTableModule,
    PoLoadingModule,
    PoTagModule,
    PoPageSlideModule,
    PoFieldModule
  ],
  templateUrl: './notas-list.html',
  styleUrls: ['./notas-list.css'],
})
export class NotasListComponent implements OnInit {

  @ViewChild('notasTable', { static: true }) notasTable!: PoTableComponent;
  @ViewChild('slideFilterAdvanced') slideFilterAdvanced!: PoPageSlideComponent;

  isLoading = false;
  loadingMore = false;
  hasActiveFilters = false;
  page = 1;
  readonly pageSize = 10;
  hasMore = false;
  private filtrosAtivos: NotasPendentesFiltros = {};

  get tableHeight(): number {
    return Math.max(window.innerHeight - 320, 300);
  }

  readonly pageActions: Array<PoPageAction> = [
    { label: 'Adicionar', action: () => this.router.navigate(['/notas-adicionar']), icon: 'an an-plus' },
    { label: 'Executar em Lote', action: () => this.executarEmLote(), icon: 'an an-arrow-right' },
    { label: 'Atualizar', action: () => this.carregar(), icon: 'an an-arrows-clockwise' }
  ];

  readonly filterSettings: PoPageFilter = {
    action: this.filterAction.bind(this),
    placeholder: 'Pesquisar notas...'
  };

  readonly ocultoOptions: PoSelectOption[] = [
    { label: 'Sim', value: 'Sim' },
    { label: 'Nao', value: 'Nao' }
  ];

  readonly tipoOptions: PoSelectOption[] = [
    { label: 'NFSe', value: 'NFSe' },
    { label: 'CTe',  value: 'CTe'  }
  ];

  // Tomadores (mesma lista do select da inclusao manual). value=CNPJ do
  // tomador; convertido para codigo da filial Protheus no confirmAdvancedFilter
  // via FilialService.codigoByCnpj antes de bater no backend.
  tomadorOptions: PoSelectOption[] = [];

  filterForm: FormGroup;

  columns: Array<PoTableColumn> = [
    { property: 'numeroExibe', label: 'Número', type: 'link', action: (_: string, row: NotaRow) => this.openDetalhe(row) },
    { property: 'numeroRps', label: 'Nº RPS', width: '130px' },
    { property: 'tipo', label: 'Tipo', width: '100px' },
    { property: 'fornecedor', label: 'Fornecedor', width: '320px' },
    { property: 'processo', label: 'Processo', width: '140px' },
    { property: 'status', label: 'Status', type: 'label', width: '120px', labels: [
      { value: 'Pendente', color: 'color-08', label: 'Pendente' }
    ]},
    { property: 'total', label: 'Total', type: 'currency', format: 'BRL', width: '140px' },
    { property: 'emissao', label: 'Emissão', type: 'date', width: '120px' },
    { property: 'recebida', label: 'Recebida', type: 'date', width: '140px' },
    { property: 'origem', label: 'Origem', width: '115px', type: 'label', labels: [
      { value: 'Transmite', color: 'color-11', label: 'Transmite' }
    ]},
    { property: 'oculto', label: 'Oculto', width: '110px' }
  ];

  notas: NotaRow[] = [];
  notasFiltered: NotaRow[] = [];

  // Sai true quando o usuario tem o privilegio OCULTO na rota /notas-list.
  // Controla o filtro 'Oculto' e a coluna Oculto na tabela. Sem priv, o
  // backend ja garante que notas ocultas nao chegam.
  canViewOcultos = false;

  constructor(
    private router: Router,
    private notif: PoNotificationService,
    private fb: FormBuilder,
    private service: NotasPendentesService,
    private loginService: LoginService,
    private filialService: FilialService
  ) {
    this.filterForm = this.buildFilterForm();
    this.filterForm.valueChanges.subscribe(values => {
      this.hasActiveFilters = Object.values(values).some(v => v !== null && v !== '');
    });
    this.canViewOcultos = this.loginService.hasPrivilegeByRota('/notas-list', 'OCULTO');
    if (!this.canViewOcultos) {
      // Remove a coluna Oculto pra usuarios sem priv
      this.columns = this.columns.filter(c => c.property !== 'oculto');
    }
  }

  ngOnInit(): void {
    this.carregar();
    // Lista de tomadores (= filiais Protheus). Mesma fonte usada na
    // inclusao manual; cache compartilhado no FilialService.
    this.filialService.filiais$.subscribe(list => {
      this.tomadorOptions = list.map(f => ({
        label: (f.cnpj ? f.cnpj + ' - ' : '') + f.nome,
        value: f.cnpj
      }));
    });
    this.filialService.loadAll().subscribe({
      error: () => this.notif.warning('Nao foi possivel carregar a lista de tomadores.')
    });
  }

  // Regra do numero exibido na coluna "Número": prioriza a NF-e (DS_NFELETR /
  // numeroNfe); senao o DS_DOC (numero). Em ambos aplica limparNumeroNf (tira
  // "T", tira "2026", ultimos 9 chars) - mesma logica da aprovacao em lote,
  // entao o numero exibido bate com o F1_DOC que sera efetivado.
  // numeroExibe e' SO exibicao; openDetalhe continua usando row.numero (chave).
  private toRows(data: NotaPendente[]): NotaRow[] {
    return data.map(n => {
      const numeroExibe = limparNumeroNf(n.numeroNfe || n.numero || '');
      return { ...n, numeroExibe };
    });
  }

  carregar(): void {
    this.page = 1;
    this.notas = [];
    this.notasFiltered = [];
    this.hasMore = false;
    this.isLoading = true;
    this.service.list(this.page, this.pageSize, this.filtrosAtivos).subscribe({
      next: res => {
        this.notas = this.toRows(res.data);
        this.notasFiltered = [...this.notas];
        this.hasMore = res.hasNext;
        this.isLoading = false;
      },
      error: () => {
        this.notif.error('Erro ao carregar notas pendentes.');
        this.isLoading = false;
      }
    });
  }

  carregarMais(): void {
    if (!this.hasMore || this.loadingMore) return;
    this.loadingMore = true;
    this.page++;
    this.service.list(this.page, this.pageSize, this.filtrosAtivos).subscribe({
      next: res => {
        this.notas = [...this.notas, ...this.toRows(res.data)];
        this.notasFiltered = [...this.notas];
        this.hasMore = res.hasNext;
        this.loadingMore = false;
      },
      error: () => {
        this.notif.error('Erro ao carregar mais notas.');
        this.loadingMore = false;
        this.page--;
      }
    });
  }

  openDetalhe(nota: NotaRow): void {
    // Abre o detalhe numa nova aba pra preservar a listagem (filtros + scroll)
    // ao voltar. Como nao usamos router.navigate, montamos a URL via UrlTree.
    const tree = this.router.createUrlTree(['/nota-detalhe', nota.numero], {
      queryParams: {
        // Chave (necessaria pra anexos e download)
        filial: nota.filial,
        serie:  nota.serie,
        fornec: nota.fornecedorCod,
        loja:   nota.fornecedorLoja,
        // NF-e (DS_NFELETR): usada na aprovacao individual para trocar o
        // numero da nota (T... -> numero da NF-e), igual ao Executar Lote.
        numeroNfe: nota.numeroNfe,
        tipo:   nota.tipo,
        emissao: nota.emissao,
        impostoPIS: nota.impostoPIS,
        impostoCOFINS: nota.impostoCOFINS,
        impostoIR:     nota.impostoIR,
        impostoINSS:   nota.impostoINSS,
        impostoCSLL:   nota.impostoCSLL,
        total:         nota.total
      }
    });
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  executarEmLote(): void {
    const selecionadas = this.notasFiltered.filter(item => item.$selected);
    if (selecionadas.length === 0) {
      this.notif.warning('Selecione ao menos um documento.');
      return;
    }
    this.router.navigate(['/executar-lote'], {
      state: { notas: selecionadas },
    });
  }

  advancedFilterAction(): void {
    this.slideFilterAdvanced.open();
  }

  private buildFilterForm(): FormGroup {
    return this.fb.group({
      numero: [''],
      rps: [''],
      fornecedor: [''],
      tomadorCnpj: [''],
      oculto: [''],
      tipo: [''],
      emissaoDe: [''],
      emissaoAte: [''],
      totalDe: [null],
      totalAte: [null]
    });
  }

  confirmAdvancedFilter(): void {
    const v = this.filterForm.value;
    // tomadorCnpj guarda o CNPJ do tomador escolhido no select. O backend
    // espera o codigo da filial Protheus (SDS.DS_FILIAL), entao resolvemos
    // o CNPJ -> codigo via FilialService.codigoByCnpj.
    const filialCod = v.tomadorCnpj ? this.filialService.codigoByCnpj(v.tomadorCnpj) : '';
    this.filtrosAtivos = {
      numero:     v.numero     || undefined,
      rps:        v.rps        || undefined,
      fornecedor: v.fornecedor || undefined,
      filial:     filialCod    || undefined,
      oculto:     v.oculto     || undefined,
      tipo:       v.tipo       || undefined,
      emissaoDe:  v.emissaoDe  || undefined,
      emissaoAte: v.emissaoAte || undefined,
      totalDe:    v.totalDe    ?? undefined,
      totalAte:   v.totalAte   ?? undefined,
    };
    this.slideFilterAdvanced.close();
    this.carregar();
  }

  cancelAdvancedFilter(): void {
    this.slideFilterAdvanced.close();
  }

  clearAdvancedFilter(): void {
    this.filterForm.reset(this.buildFilterForm().value);
    this.hasActiveFilters = false;
    this.filtrosAtivos = {};
    this.carregar();
  }

  private filterAction(filter: string): void {
    // Pesquisa simples na barra superior. O termo eh aplicado no backend com
    // LIKE (OR) em DS_DOC, DS_NUMRPS e DS_NFELETR, via parametro 'busca'.
    const term = (filter || '').trim();
    this.filtrosAtivos = {
      ...this.filtrosAtivos,
      busca: term || undefined
    };
    this.carregar();
  }
}
